import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { EXTRA_DICTS } from "./translations";

export const LANGUAGES = [
  { code: "hi", native: "हिंदी", en: "Hindi", full: true },
  { code: "en", native: "English", en: "English", full: true },
  { code: "mr", native: "मराठी", en: "Marathi" },
  { code: "bn", native: "বাংলা", en: "Bengali" },
  { code: "ta", native: "தமிழ்", en: "Tamil" },
  { code: "te", native: "తెలుగు", en: "Telugu" },
  { code: "kn", native: "ಕನ್ನಡ", en: "Kannada" },
  { code: "gu", native: "ગુજરાતી", en: "Gujarati" },
  { code: "ml", native: "മലയാളം", en: "Malayalam" },
  { code: "pa", native: "ਪੰਜਾਬੀ", en: "Punjabi" },
];

const en = {
  app_name: "My Manger",
  tagline: "Your trusted home partner",
  value_prop: "Rent, agreements & tenants — managed simply, in your language.",
  wa_support: "Chat with us on WhatsApp",
  pick_language: "Choose your language",
  pick_language_sub: "You can change this anytime in Settings",
  continue: "Continue",
  login: "Log in",
  signup: "Create account",
  logout: "Log out",
  email: "Email",
  phone: "Phone number",
  password: "Password",
  full_name: "Your name",
  i_am: "I am a…",
  owner: "Owner (Landlord)",
  tenant: "Tenant",
  have_account: "Already have an account?",
  no_account: "New here?",
  welcome: "Welcome",
  // nav
  nav_home: "Home",
  nav_properties: "Properties",
  nav_ai: "Sathi AI",
  nav_payments: "Payments",
  nav_profile: "Profile",
  ask_sathi: "Ask Sathi",
  // roles
  landlord_mode: "Landlord",
  tenant_mode: "Tenant",
  // home owner
  rent_collected: "Rent collected",
  rent_pending: "Rent pending",
  add_property: "Add Property",
  gen_agreement: "New Agreement",
  send_reminder: "Rent Reminder",
  track_complaints: "Complaints",
  my_properties: "My properties",
  open_tickets: "Open maintenance",
  upcoming_renewals: "Upcoming renewals",
  // home tenant
  rent_due: "Rent due",
  pay_upi: "Pay via UPI",
  my_agreement: "My agreement",
  read_clauses: "Read simple clauses",
  raise_complaint: "Raise complaint",
  request_renewal: "Renewal",
  deposit_balance: "Deposit balance",
  payment_history: "Payment history",
  // listing
  find_home: "Find a home",
  filters: "Filters",
  budget_max: "Max budget",
  city: "City",
  furnishing: "Furnishing",
  best_matches: "Best matches for you",
  book_visit: "Book a visit",
  ask_about_home: "Ask about this home",
  per_month: "/month",
  deposit: "Deposit",
  // wizard
  list_property: "List your property",
  step: "Step",
  basic_info: "Basic info",
  photos_location: "Photos & Location",
  amenities_rules: "Amenities & Rules",
  ai_rent: "AI rent suggestion",
  title: "Listing title",
  bhk: "Configuration",
  area: "Area (sqft)",
  rent: "Monthly rent",
  locality: "Locality / Area",
  state: "State",
  amenities: "Amenities",
  house_rules: "House rules",
  tenant_pref: "Preferred tenant",
  add_photos: "Add photos",
  available_from: "Available from",
  nearby: "Nearby (metro, school)",
  get_ai_rent: "Get AI rent suggestion",
  publish: "Publish listing",
  discrimination_warn: "Please keep rules fair. Refusing tenants by religion, caste or region can be unlawful.",
  // kyc
  verify_identity: "Verify your identity",
  why_kyc: "Verification builds trust so owners and tenants feel safe dealing with each other.",
  aadhaar: "Aadhaar",
  pan: "PAN",
  send_otp: "Send OTP",
  verify: "Verify",
  enter_otp: "Enter OTP",
  verified: "Verified",
  not_verified: "Not verified",
  police_verification: "Police verification",
  file_police: "File police verification",
  trust_badge: "Trust badge",
  // agreement
  agreement: "Rent Agreement",
  explain_simply: "Tap to explain simply",
  esign: "e-Sign with Aadhaar",
  stamp_duty: "Stamp duty",
  submit_authority: "Submit to Rent Authority",
  // payments
  pay_rent: "Pay rent",
  receipt: "Receipt",
  download_receipt: "Download receipt",
  tds_assistant: "TDS assistant",
  // maintenance
  maintenance: "Maintenance",
  describe_issue: "Describe the problem",
  speak: "Speak",
  submit: "Submit",
  urgency: "Urgency",
  category: "Category",
  status: "Status",
  // condition
  condition_report: "Condition report",
  movein: "Move-in",
  moveout: "Move-out",
  compare_ai: "AI compare",
  confirm: "Confirm",
  // common
  save: "Save",
  cancel: "Cancel",
  next: "Next",
  back: "Back",
  loading: "Loading…",
  what_means: "What does this mean?",
  why_matters: "Why this matters",
  settings: "Settings",
  language: "Language",
  large_text: "Large text",
  high_contrast: "High contrast",
  encrypted_note: "Your documents are encrypted and safe.",
  help_support: "Help & Support",
  type_message: "Type your message…",
  send: "Send",
  forgot_password: "Forgot password?",
  forgot_desc: "Enter your email and we'll send you a link to reset your password.",
  send_reset_link: "Send reset link",
  reset_email_sent: "Check your email for the reset link",
  back_to_login: "Back to login",
  reset_password: "Set a new password",
  reset_desc: "Choose a new password for your account.",
  new_password: "New password",
  confirm_password: "Confirm password",
  update_password: "Update password",
  reset_success: "Password updated! Please log in.",
  password_min: "Password must be at least 6 characters.",
  password_mismatch: "Passwords do not match.",
  rent_reminders: "Rent reminders",
  preview_reminder: "Preview reminder",
};

// Hindi (full)
const hi = {
  app_name: "My Manger",
  tagline: "आपका भरोसेमंद घर साथी",
  value_prop: "किराया, एग्रीमेंट और किराएदार — आसान तरीके से, आपकी भाषा में।",
  wa_support: "WhatsApp पर हमसे बात करें",
  pick_language: "अपनी भाषा चुनें",
  pick_language_sub: "आप इसे सेटिंग्स में कभी भी बदल सकते हैं",
  continue: "आगे बढ़ें",
  login: "लॉग इन करें",
  signup: "खाता बनाएं",
  logout: "लॉग आउट",
  email: "ईमेल",
  phone: "फ़ोन नंबर",
  password: "पासवर्ड",
  full_name: "आपका नाम",
  i_am: "मैं हूँ…",
  owner: "मालिक (मकान मालिक)",
  tenant: "किराएदार",
  have_account: "पहले से खाता है?",
  no_account: "नए हैं?",
  welcome: "स्वागत है",
  nav_home: "होम",
  nav_properties: "प्रॉपर्टी",
  nav_ai: "साथी AI",
  nav_payments: "भुगतान",
  nav_profile: "प्रोफ़ाइल",
  ask_sathi: "साथी से पूछें",
  landlord_mode: "मकान मालिक",
  tenant_mode: "किराएदार",
  rent_collected: "किराया मिला",
  rent_pending: "किराया बाकी",
  add_property: "प्रॉपर्टी जोड़ें",
  gen_agreement: "नया एग्रीमेंट",
  send_reminder: "किराया याद दिलाएं",
  track_complaints: "शिकायतें",
  my_properties: "मेरी प्रॉपर्टी",
  open_tickets: "खुली मरम्मत",
  upcoming_renewals: "आने वाले रिन्युअल",
  rent_due: "किराया देना है",
  pay_upi: "UPI से भुगतान करें",
  my_agreement: "मेरा एग्रीमेंट",
  read_clauses: "आसान शर्तें पढ़ें",
  raise_complaint: "शिकायत करें",
  request_renewal: "रिन्युअल",
  deposit_balance: "जमा राशि",
  payment_history: "भुगतान इतिहास",
  find_home: "घर खोजें",
  filters: "फ़िल्टर",
  budget_max: "अधिकतम बजट",
  city: "शहर",
  furnishing: "फर्निशिंग",
  best_matches: "आपके लिए सबसे अच्छे घर",
  book_visit: "विज़िट बुक करें",
  ask_about_home: "इस घर के बारे में पूछें",
  per_month: "/माह",
  deposit: "जमा (डिपॉज़िट)",
  list_property: "अपनी प्रॉपर्टी लिस्ट करें",
  step: "चरण",
  basic_info: "बुनियादी जानकारी",
  photos_location: "फ़ोटो और स्थान",
  amenities_rules: "सुविधाएं और नियम",
  ai_rent: "AI किराया सुझाव",
  title: "लिस्टिंग का शीर्षक",
  bhk: "कॉन्फ़िगरेशन",
  area: "क्षेत्रफल (वर्ग फुट)",
  rent: "मासिक किराया",
  locality: "इलाका / क्षेत्र",
  state: "राज्य",
  amenities: "सुविधाएं",
  house_rules: "घर के नियम",
  tenant_pref: "पसंदीदा किराएदार",
  add_photos: "फ़ोटो जोड़ें",
  available_from: "कब से उपलब्ध",
  nearby: "आस-पास (मेट्रो, स्कूल)",
  get_ai_rent: "AI किराया सुझाव लें",
  publish: "लिस्टिंग प्रकाशित करें",
  discrimination_warn: "कृपया नियम निष्पक्ष रखें। धर्म, जाति या क्षेत्र के आधार पर मना करना गैरकानूनी हो सकता है।",
  verify_identity: "अपनी पहचान सत्यापित करें",
  why_kyc: "सत्यापन से भरोसा बढ़ता है ताकि मालिक और किराएदार सुरक्षित महसूस करें।",
  aadhaar: "आधार",
  pan: "पैन",
  send_otp: "OTP भेजें",
  verify: "सत्यापित करें",
  enter_otp: "OTP डालें",
  verified: "सत्यापित",
  not_verified: "सत्यापित नहीं",
  police_verification: "पुलिस सत्यापन",
  file_police: "पुलिस सत्यापन दर्ज करें",
  trust_badge: "भरोसा बैज",
  agreement: "किराया एग्रीमेंट",
  explain_simply: "आसान भाषा में समझें",
  esign: "आधार से e-Sign करें",
  stamp_duty: "स्टाम्प ड्यूटी",
  submit_authority: "रेंट अथॉरिटी को भेजें",
  pay_rent: "किराया दें",
  receipt: "रसीद",
  download_receipt: "रसीद डाउनलोड करें",
  tds_assistant: "TDS सहायक",
  maintenance: "मरम्मत",
  describe_issue: "समस्या बताएं",
  speak: "बोलें",
  submit: "जमा करें",
  urgency: "तात्कालिकता",
  category: "श्रेणी",
  status: "स्थिति",
  condition_report: "स्थिति रिपोर्ट",
  movein: "मूव-इन",
  moveout: "मूव-आउट",
  compare_ai: "AI तुलना",
  confirm: "पुष्टि करें",
  save: "सेव करें",
  cancel: "रद्द करें",
  next: "आगे",
  back: "पीछे",
  loading: "लोड हो रहा है…",
  what_means: "इसका क्या मतलब है?",
  why_matters: "यह क्यों ज़रूरी है",
  settings: "सेटिंग्स",
  language: "भाषा",
  large_text: "बड़ा टेक्स्ट",
  high_contrast: "हाई कॉन्ट्रास्ट",
  encrypted_note: "आपके दस्तावेज़ एन्क्रिप्टेड और सुरक्षित हैं।",
  help_support: "मदद और सहायता",
  type_message: "अपना संदेश लिखें…",
  send: "भेजें",
  forgot_password: "पासवर्ड भूल गए?",
  forgot_desc: "अपना ईमेल डालें, हम आपको पासवर्ड रीसेट करने का लिंक भेजेंगे।",
  send_reset_link: "रीसेट लिंक भेजें",
  reset_email_sent: "रीसेट लिंक के लिए अपना ईमेल देखें",
  back_to_login: "लॉग इन पर वापस जाएं",
  reset_password: "नया पासवर्ड बनाएं",
  reset_desc: "अपने खाते के लिए नया पासवर्ड चुनें।",
  new_password: "नया पासवर्ड",
  confirm_password: "पासवर्ड की पुष्टि करें",
  update_password: "पासवर्ड अपडेट करें",
  reset_success: "पासवर्ड अपडेट हो गया! कृपया लॉग इन करें।",
  password_min: "पासवर्ड कम से कम 6 अक्षरों का होना चाहिए।",
  password_mismatch: "पासवर्ड मेल नहीं खाते।",
  rent_reminders: "किराया रिमाइंडर",
  preview_reminder: "रिमाइंडर देखें",
};

const DICTS = { en, hi, ...EXTRA_DICTS };

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("gs_lang") || "");
  const setLang = useCallback((code) => {
    setLangState(code);
    localStorage.setItem("gs_lang", code);
  }, []);
  const t = useCallback(
    (key) => (DICTS[lang] && DICTS[lang][key]) || DICTS.en[key] || key,
    [lang]
  );
  useEffect(() => {
    const large = localStorage.getItem("gs_large") === "1";
    const contrast = localStorage.getItem("gs_contrast") === "1";
    document.documentElement.classList.toggle("gs-large", large);
    document.documentElement.classList.toggle("gs-contrast", contrast);
  }, []);
  const value = useMemo(() => ({ lang, setLang, t, hasLang: !!lang }), [lang, setLang, t]);
  return (
    <LangContext.Provider value={value}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
